import { useState, useEffect } from "react";
import { CheckCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { routes } from "wasp/client/router";
import { Button } from "../../client/components/ui/button";
import { BeamButton } from "./BeamButton";
import { cn } from "../../client/utils";
import useTranslation from "../../client/hooks/useTranslation";
import { PaymentPlanId } from "../../payment/plans";
import { getPaymentPlanCards } from "../../payment/PricingPage";
import ScrollAnimation from "./ScrollAnimation";
import { trackLandingCta, trackPricingViewed } from '../../client/utils/posthog';

const PLAN_ORDER = [PaymentPlanId.Free, PaymentPlanId.Pro, PaymentPlanId.Max];

const PLAN_CONFIG: Record<PaymentPlanId, {
  accentColor: string;
  glowColor: string;
  featured: boolean;
  badgeLabel?: string;
  badgeColor?: string;
}> = {
  [PaymentPlanId.Free]: {
    accentColor: "hsl(var(--border))",
    glowColor: "transparent",
    featured: false,
  },
  [PaymentPlanId.Pro]: {
    accentColor: "#00C9A7",
    glowColor: "#00C9A7",
    featured: true,
    badgeColor: "#00C9A7",
  },
  [PaymentPlanId.Max]: {
    accentColor: "#F59E0B",
    glowColor: "#F59E0B",
    featured: false,
    badgeColor: "#F59E0B",
  },
  [PaymentPlanId.ProAnnual]: {
    accentColor: "#00C9A7",
    glowColor: "#00C9A7",
    featured: true,
    badgeColor: "#00C9A7",
  },
  [PaymentPlanId.MaxAnnual]: {
    accentColor: "#F59E0B",
    glowColor: "#F59E0B",
    featured: false,
    badgeColor: "#F59E0B",
  },
};

export default function Pricing() {
  const t = useTranslation();
  const navigate = useNavigate();
  const paymentPlanCards = getPaymentPlanCards(t);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');

  useEffect(() => {
    trackPricingViewed();
  }, []);

  function resolvedPlanId(planId: PaymentPlanId): PaymentPlanId {
    if (billingPeriod === 'annual') {
      if (planId === PaymentPlanId.Pro) return PaymentPlanId.ProAnnual;
      if (planId === PaymentPlanId.Max) return PaymentPlanId.MaxAnnual;
    }
    return planId;
  }

  function handlePlanClick(planId: PaymentPlanId) {
    const planKey = planId === PaymentPlanId.Free ? 'free'
      : planId === PaymentPlanId.Pro ? 'pro'
      : 'max';
    trackLandingCta('pricing', planKey);
    if (planId === PaymentPlanId.Free) {
      navigate(routes.SignupRoute.to);
    } else {
      const resolved = resolvedPlanId(planId);
      navigate(`${routes.SignupRoute.to}?plan=${resolved}`);
    }
  }

  return (
    <div id="pricing" className="bg-background py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <ScrollAnimation direction="up">
          <div className="mx-auto max-w-4xl text-center">
            <p className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              {t.landing.pricing.title}
            </p>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              {t.landing.pricing.subtitle}
            </p>
          </div>
        </ScrollAnimation>

        {/* Billing period toggle */}
        <div className="flex items-center justify-center mt-8 mb-4">
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-card/50 backdrop-blur-sm p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200",
                billingPeriod === 'monthly'
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.pricing.monthly}
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={cn(
                "flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200",
                billingPeriod === 'annual'
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.pricing.annual}
              <span className="rounded-full bg-[#00C9A7]/15 px-2 py-0.5 text-[10px] font-semibold text-[#00C9A7]">
                {t.pricing.savePercent}
              </span>
            </button>
          </div>
        </div>

        <div className="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-6 sm:mt-20 sm:grid-cols-3 items-stretch">
          {PLAN_ORDER.map((planId, index) => {
            const config = PLAN_CONFIG[planId];
            const displayPlanId = billingPeriod === 'annual'
              ? (planId === PaymentPlanId.Pro ? PaymentPlanId.ProAnnual
                : planId === PaymentPlanId.Max ? PaymentPlanId.MaxAnnual
                : planId)
              : planId;
            const card = paymentPlanCards[displayPlanId];

            return (
              <ScrollAnimation key={planId} direction="up" delay={index * 80}>
                <div
                  className={cn(
                    "group relative flex flex-col h-full overflow-hidden rounded-xl transition-all duration-300",
                    "border bg-card/50 backdrop-blur-sm",
                    config.featured
                      ? "border-[#00C9A7]/40 shadow-lg shadow-[#00C9A7]/10"
                      : "border-white/5 hover:border-white/10",
                  )}
                >
                  {/* Featured glow ring */}
                  {config.featured && (
                    <div
                      className="pointer-events-none absolute inset-0 rounded-xl opacity-30"
                      style={{
                        boxShadow: `inset 0 0 30px ${config.glowColor}20`,
                      }}
                    />
                  )}

                  {/* Top accent line */}
                  <div
                    className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl"
                    style={{
                      background: config.featured
                        ? `linear-gradient(90deg, transparent, ${config.accentColor}, transparent)`
                        : planId === PaymentPlanId.Pro
                        ? `linear-gradient(90deg, transparent, ${config.accentColor}80, transparent)`
                        : "transparent",
                    }}
                  />

                  <div className="relative z-10 flex h-full flex-col p-6 sm:p-8 pt-7">
                    {/* Plan name */}
                    <div className="mb-1 flex items-center gap-2">
                      <h3
                        className="text-xl font-bold"
                        style={
                          config.featured
                            ? { color: config.accentColor }
                            : planId === PaymentPlanId.Pro
                            ? { color: config.accentColor }
                            : { color: "hsl(var(--foreground))" }
                        }
                      >
                        {card.name}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-muted-foreground text-sm leading-5 mb-6">
                      {card.description}
                    </p>

                    {/* Price */}
                    <div className="flex items-baseline gap-x-1 mb-2">
                      <span
                        className={cn(
                          "font-bold tracking-tight",
                          config.featured ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl",
                        )}
                        style={
                          config.featured
                            ? { color: config.accentColor }
                            : { color: "hsl(var(--foreground))" }
                        }
                      >
                        {card.price}
                      </span>
                      {(planId === PaymentPlanId.Pro || planId === PaymentPlanId.Max) && (
                        <span className="text-muted-foreground text-sm font-semibold leading-6">
                          {t.pricing.perMonth}
                        </span>
                      )}
                    </div>

                    {billingPeriod === 'annual' && card.savings && (
                      <p className="text-xs text-[#00C9A7] font-medium -mt-1 mb-2">
                        {card.savings}
                      </p>
                    )}

                    {/* Pro badge */}
                    {planId === PaymentPlanId.Pro && card.badge && (
                      <div
                        className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium w-fit border"
                        style={{
                          color: config.accentColor,
                          borderColor: `${config.accentColor}30`,
                          backgroundColor: `${config.accentColor}10`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: config.accentColor }} />
                        {card.badge}
                      </div>
                    )}

                    {/* Features list */}
                    <ul role="list" className="flex-1 space-y-2.5 text-sm leading-5 mb-8">
                      {card.features.map((feature) => {
                        const label = typeof feature === "string" ? feature : feature.label;
                        const badge = typeof feature === "string" ? undefined : feature.badge;
                        return (
                          <li key={label} className="flex items-start gap-x-2.5">
                            {badge === "soon" ? (
                              <Clock className="mt-0.5 h-4 w-4 flex-none shrink-0 text-muted-foreground/60" />
                            ) : (
                              <CheckCircle
                                className="mt-0.5 h-4 w-4 flex-none shrink-0"
                                style={{
                                  color: config.featured
                                    ? config.accentColor
                                    : planId === PaymentPlanId.Pro
                                    ? config.accentColor
                                    : "hsl(var(--primary))",
                                }}
                              />
                            )}
                            <span
                              className={cn(
                                "flex-1 flex flex-wrap items-center gap-1.5",
                                badge === "soon" ? "text-muted-foreground/60" : "text-muted-foreground",
                              )}
                            >
                              {label}
                              {badge === "soon" && (
                                <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-primary">
                                  {t.common.soon}
                                </span>
                              )}
                              {badge === "beta" && (
                                <span className="inline-flex items-center rounded-full border border-[#EF9F27]/40 bg-[#EF9F27]/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-[#EF9F27]">
                                  {t.common.beta}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* CTA */}
                    {planId === PaymentPlanId.Free ? (
                      <Button
                        onClick={() => handlePlanClick(planId)}
                        variant="outline"
                        className="w-full transition-all duration-300 hover:border-primary/40"
                        size="lg"
                      >
                        {t.landing.pricing.ctaFree}
                      </Button>
                    ) : planId === PaymentPlanId.Pro ? (
                      <BeamButton fullWidth onClick={() => handlePlanClick(planId)}>
                        {t.landing.pricing.ctaPro}
                      </BeamButton>
                    ) : (
                      <Button
                        onClick={() => handlePlanClick(planId)}
                        variant="outline"
                        className="w-full transition-all duration-300"
                        style={{
                          borderColor: `${config.accentColor}40`,
                          color: config.accentColor,
                        }}
                        size="lg"
                      >
                        {t.landing.pricing.ctaMax}
                      </Button>
                    )}
                  </div>
                </div>
              </ScrollAnimation>
            );
          })}
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          {t.landing.pricing.trustCopy}
        </p>
        <p className="mt-3 text-center text-sm text-muted-foreground/70 italic">
          {t.landing.pricing.disclaimer}
        </p>
      </div>
    </div>
  );
}
